/**
 * CreateAccountWithEmail Component
 * 
 * This component is responsible for rendering the sign-up form for creating an account using email.
 * It includes functionalities such as:
 * - Capturing user details like username, email, and password.
 * - Validating user input using Formik and Yup.
 * - Checking for username availability via API calls.
 * - Creating a new user account and navigating to the thank-you page upon success.
 * 
 * Dependencies:
 * - React hooks (useState) for managing loading state.
 * - Formik for form handling and validation.
 * - Yup for schema-based validation.
 * - Custom components like ALTTextField.
 * - Services for checking username availability and creating a user.
 * 
 * Note:
 * - The component uses toast notifications to provide feedback on form submission.
 * - Secure local storage is used to temporarily store the registered email address.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import GoogleIcon from '@mui/icons-material/Google';
import AppleIcon from '@mui/icons-material/Apple';
import EmailIcon from '@mui/icons-material/Email';
import { checkUserNameExcistsThroughHorizon, setUser } from "../services/services";
import { ToastContainer, toast } from 'react-toastify';
import * as yup from 'yup';
import { LoadingButton } from "@mui/lab";
import { validationToastMessages } from "../../utils/toastMessages";
import { validationMessages } from "../../utils/validationMessages";
import { addCustomYupMethods } from "../../common/input-fields/utils/CustomYupValidations";
import { useFormik } from "formik";
import ALTTextField from "../../common/input-fields/ALTTextField";
import { FieldVariants, textFormatTypeEnum } from "../../common/input-fields/utils/appInputenum";
import LoginButton from "./LoginButton";

const CreateAccountWithEmail = () => {

  //#region initialdata
  const initialData = {
    userName: '',
    emailAddress: '',
    password: '',
    confirmPassword: ''
  }

  //#region variables
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  //#region change events
  const handleChange = (name, value) => {
    userDetails.setFieldValue(name, value);
  }

  //#region click events
  const onSaveClick = () => {
    handleSubmit();
  }

  //#region api post calls
  const handleSubmit = async () => {
    setLoading(true);
    const userNameData = await checkUserNameExcistsThroughHorizon(userDetails.values.userName)
    secureLocalStorage.setItem("registeredMailID", userDetails.values.emailAddress)
    if (userNameData.responseCode === 200) {
      toast.error(validationToastMessages.USER_NAME_ALREADY_EXISTS, {
        position: 'position-bottom',
        theme: "colored",
      });
      setLoading(false);
    }
    else {
      const requestedBody = {
        firstName: "",
        lastName: "",
        userName: userDetails.values.userName,
        emailAddress: userDetails.values.emailAddress,
        password: userDetails.values.password,
        userRoleId: 0,
        isActive: false,
        softDelete: false,
        createdBy: 0,
        createdDate: "2023-04-13T07:22:40.189Z",
        updatedBy: 0,
        updatedDate: "2023-04-13T07:22:40.189Z"
      }
      const data = await setUser(requestedBody);
      if (data.responseCode === 200) {
        navigate('/thank-you');
      }
      else if (data.responseCode === 422 || data.responseCode === 409) {
        let errorMessage = "This email address is already in use. Please choose different one";
        if (data.responseData == "Invalid value specified for property 'mail' of resource 'User'.") {
          errorMessage = "Please provide valid Email Address.";
        }
        toast.error(errorMessage, {
          position: 'position-bottom',
          theme: "colored",
        });
        setLoading(false);
      }
      else {
        toast.error(validationToastMessages.UNABLE_TO_CREATE_USER, {
          position: 'position-bottom',
          theme: "colored",
        });
        setLoading(false);
      }
    }
  };

  //#region formik validations
  addCustomYupMethods()

  const signupValidationSchema = yup.object().shape({
    userName: yup.string()
      .required(validationMessages.REQUIRED)
      .customUserName(validationMessages.USER_NAME),
    emailAddress: yup
      .string()
      .required(validationMessages.REQUIRED)
      .customEmail(validationMessages.EMAIL_ADDRESS),
    password: yup.string()
      .required(validationMessages.REQUIRED)
      .customPassword(validationMessages.PASSWORD),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password'), null], validationMessages.PASSWORD_MATCH)
      .required(validationMessages.REQUIRED),
  });

  const userDetails = useFormik({
    initialValues: initialData,
    validationSchema: signupValidationSchema,
    onSubmit: (values) => {
      onSaveClick(values);
    },
  });

  //#region return
  return (
    <div style={{ width: '100%' }}>
      <div className="login">
        <div className="leftbg">
          <img className="res-logo" src="/images/Alt360-Logo-white.png" alt="Alt360" />
        </div>
        <div className="loadspace">
          <div className="display-center">
            <form onSubmit={handleSubmit} novalidate="novalidate">
              <div className="signup-header">
                <h3>Create an Account</h3>
                <p>Transform documents into actionable investment strategies </p>
              </div>
              <div className="child-margin-15">
                <div>
                  <ALTTextField
                    variant={FieldVariants.STANDARD}
                    name="userName"
                    label="UserName"
                    value={userDetails.values.userName}
                    onChange={(name, value) => handleChange(name, value)}
                    required={true}
                    onBlur={userDetails.handleBlur}
                    error={userDetails.touched.userName && Boolean(userDetails.errors.userName)}
                    errorMessage={userDetails.touched.userName && userDetails.errors.userName} />
                </div>
                <div>
                  <ALTTextField
                    variant={FieldVariants.STANDARD}
                    name="emailAddress"
                    label="Email Address"
                    value={userDetails.values.emailAddress}
                    onChange={(name, value) => handleChange(name, value)}
                    required={true}
                    onBlur={userDetails.handleBlur}
                    error={userDetails.touched.emailAddress && Boolean(userDetails.errors.emailAddress)}
                    errorMessage={userDetails.touched.emailAddress && userDetails.errors.emailAddress} />
                </div>
                <div>
                  <ALTTextField
                    variant={FieldVariants.STANDARD}
                    textFormatType={textFormatTypeEnum.PASSWORD}
                    name="password"
                    label="Password"
                    value={userDetails.values.password}
                    onChange={(name, value) => handleChange(name, value)}
                    required={true}
                    onBlur={userDetails.handleBlur}
                    error={userDetails.touched.password && Boolean(userDetails.errors.password)}
                    errorMessage={userDetails.touched.password && userDetails.errors.password} />
                </div>
                <div>
                  <ALTTextField
                    variant={FieldVariants.STANDARD}
                    textFormatType={textFormatTypeEnum.PASSWORD}
                    name="confirmPassword"
                    label="Confirm Password"
                    value={userDetails.values.confirmPassword}
                    onChange={(name, value) => handleChange(name, value)}
                    required={true}
                    onBlur={userDetails.handleBlur}
                    error={userDetails.touched.confirmPassword && Boolean(userDetails.errors.confirmPassword)}
                    errorMessage={userDetails.touched.confirmPassword && userDetails.errors.confirmPassword} />
                </div>
              </div>
              <div className="reg-form margin-top-20">
                <LoadingButton
                  className="btn-orange mt2"
                  loading={loading}
                  loadingPosition="start"
                  onClick={userDetails.handleSubmit}>
                  <EmailIcon alt="email-logo" />
                  <span className="text-decoration-none">
                    SIGN UP
                  </span>
                </LoadingButton>
                {/* <div className="SignupWith">
                    <GoogleOAuthProvider className="btn" clientId="936331901467-lkb9pkj7mk2m940v1a5fm31t3tt8tb8c.apps.googleusercontent.com" >
                      <Google />
                    </GoogleOAuthProvider>

                    <button className="btn">
                      <AppleIcon alt="apple-logo" />
                      SIGN UP WITH APPLE
                    </button>
                  </div> */}
                <div className="recaptcha mt3 margin-top-20">
                  <span className="already-ac"> Already have an account? </span>
                    <a className="signup-LoginLink cursor-pointer">
                      <LoginButton signUpScreen={true}/>
                    </a>
                  </div>

                {/* <div className="google-recaptcha">
                    This site is protected by reCAPTCHA and the
                    <p> Google<a href="#"> Privacy Policy </a>and <a href="#"> Terms of Service </a>apply.</p>
                  </div> */}
              </div>
            </form >
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAccountWithEmail;

